/**
 * Criptografia dos stores persistidos no IndexedDB (T33).
 *
 * - AES-GCM 256 com chave por usuário, gerada com `extractable: false`: o material da
 *   chave nunca sai do WebCrypto. A chave fica como `CryptoKey` num banco IndexedDB
 *   próprio (`synap-keys`), nunca em localStorage.
 * - IV aleatório de 96 bits a cada gravação.
 * - AAD amarra o texto cifrado ao usuário e ao nome do registro: um registro copiado
 *   para outra chave ou outro usuário não decifra.
 */

export const PERSISTENCE_ENVELOPE_VERSION = 1

export type EncryptedEnvelope = {
  v: typeof PERSISTENCE_ENVELOPE_VERSION
  uid: string
  iv: Uint8Array<ArrayBuffer>
  ct: ArrayBuffer
}

const IV_BYTES = 12

const getSubtle = (): SubtleCrypto | null =>
  typeof globalThis.crypto !== 'undefined' && globalThis.crypto.subtle ? globalThis.crypto.subtle : null

export const isPersistenceCryptoAvailable = (): boolean => getSubtle() !== null

const aad = (uid: string, name: string): Uint8Array<ArrayBuffer> =>
  new Uint8Array(new TextEncoder().encode(`synap-idb:v${PERSISTENCE_ENVELOPE_VERSION}:${uid}:${name}`))

export const generatePersistenceKey = async (): Promise<CryptoKey> => {
  const subtle = getSubtle()
  if (!subtle) throw new Error('WebCrypto indisponível')
  return await subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt'])
}

export const isEncryptedEnvelope = (value: unknown): value is EncryptedEnvelope => {
  if (!value || typeof value !== 'object') return false
  const v = value as Partial<EncryptedEnvelope>
  return (
    v.v === PERSISTENCE_ENVELOPE_VERSION &&
    typeof v.uid === 'string' &&
    v.iv instanceof Uint8Array &&
    v.iv.byteLength === IV_BYTES &&
    v.ct instanceof ArrayBuffer
  )
}

export const encryptPersistedValue = async (
  key: CryptoKey,
  uid: string,
  name: string,
  plaintext: string,
): Promise<EncryptedEnvelope> => {
  const subtle = getSubtle()
  if (!subtle) throw new Error('WebCrypto indisponível')
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(new ArrayBuffer(IV_BYTES)))
  const ct = await subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: aad(uid, name) },
    key,
    new Uint8Array(new TextEncoder().encode(plaintext)),
  )
  return { v: PERSISTENCE_ENVELOPE_VERSION, uid, iv, ct }
}

/** Lança se a chave, o usuário, o nome ou o conteúdo não baterem (tag GCM inválida). */
export const decryptPersistedValue = async (
  key: CryptoKey,
  name: string,
  envelope: EncryptedEnvelope,
): Promise<string> => {
  const subtle = getSubtle()
  if (!subtle) throw new Error('WebCrypto indisponível')
  const plain = await subtle.decrypt(
    { name: 'AES-GCM', iv: envelope.iv, additionalData: aad(envelope.uid, name) },
    key,
    envelope.ct,
  )
  return new TextDecoder().decode(plain)
}
